import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import {
  uploadConnectorService,
  BatchHistoryDetail,
  UploadedImage,
} from '../services/uploadConnector';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { ImageCompareModal } from '../components/assets/ImageCompareModal';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft,
  Clock,
  Images,
  Zap,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Ban,
  Eye,
  RotateCcw,
  Filter,
} from 'lucide-react';

type ImageFilterType = 'all' | 'completed' | 'failed' | 'processing';

const getTransformationLabel = (type: string): string => {
  const labels: Record<string, string> = {
    background_removal: 'Background Removal',
    upscale: 'AI Upscale',
    upscale_2x: 'Upscale 2x',
    upscale_4x: 'Upscale 4x',
    generative_fill: 'Generative Fill',
    generative_remove: 'Generative Remove',
    generative_replace: 'Generative Replace',
    generative_recolor: 'Recolor',
    invisible_mannequin: 'Invisible Mannequin',
    virtual_staging: 'Virtual Staging',
    format_conversion: 'Format Conversion',
    gemini_studio_retouch: 'Studio Retouch',
    studio_retouch: 'Studio Retouch',
    enhance: 'Auto Enhance',
    optimize: 'Web Optimize',
    resize: 'Resize',
    quality: 'Quality Enhancement',
  };
  return labels[type] || type.replace(/_/g, ' ');
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'processing':
      return (
        <Badge className="bg-blue-600 text-white animate-pulse">
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Processing
        </Badge>
      );
    case 'completed':
      return (
        <Badge className="bg-green-600 text-white">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </Badge>
      );
    case 'partially_completed':
      return (
        <Badge className="bg-yellow-600 text-white">
          <AlertCircle className="w-3 h-3 mr-1" />
          Partially Completed
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive">
          <XCircle className="w-3 h-3 mr-1" />
          Failed
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary">
          <Clock className="w-3 h-3 mr-1" />
          {status.replace(/_/g, ' ')}
        </Badge>
      );
  }
};

export function BatchHistoryDetailPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [batch, setBatch] = useState<BatchHistoryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageFilter, setImageFilter] = useState<ImageFilterType>('all');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [compareImage, setCompareImage] = useState<UploadedImage | null>(null);
  const [isRetrying, setIsRetrying] = useState<Record<string, boolean>>({});

  const prevStatusRef = useRef<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchBatchDetail = useCallback(async () => {
    if (!batchId) return;
    try {
      const data = await uploadConnectorService.getBatchDetail(batchId);
      setBatch(data);
    } catch (err: any) {
      console.error('Failed to fetch batch detail:', err);
      // Only set batch to null if we're on initial load
      if (isLoading) {
        setBatch(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [batchId]);

  // Initial fetch
  useEffect(() => {
    fetchBatchDetail();
  }, [fetchBatchDetail]);

  // Auto-refresh when processing
  useEffect(() => {
    if (batch?.status === 'processing') {
      intervalRef.current = setInterval(() => {
        fetchBatchDetail();
      }, 3000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Detect status transition
    if (prevStatusRef.current === 'processing' && batch?.status && batch.status !== 'processing') {
      if (batch.status === 'completed') {
        toast({
          title: 'Batch completed',
          description: `${batch.processed_images} images processed successfully.`,
        });
      } else if (batch.status === 'partially_completed') {
        toast({
          title: 'Batch completed',
          description: `${batch.processed_images} images processed, ${batch.failed_images} failed.`,
        });
      } else if (batch.status === 'failed') {
        toast({
          title: 'Batch failed',
          description: batch.error_message || 'Processing failed.',
          variant: 'destructive',
        });
      }
    }

    prevStatusRef.current = batch?.status || null;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [batch?.status, batch?.processed_images, batch?.failed_images, batch?.error_message, fetchBatchDetail, toast]);

  const handleCancel = async () => {
    if (!batchId) return;
    setIsCancelling(true);
    try {
      await uploadConnectorService.cancelBatch(batchId);
      toast({
        title: 'Batch cancelled',
        description: 'Already-processed images have been kept.',
      });
      await fetchBatchDetail();
      setShowCancelDialog(false);
    } catch (err: any) {
      toast({
        title: 'Cancel failed',
        description: err.message || 'Could not cancel batch.',
        variant: 'destructive',
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRetry = async (image: UploadedImage) => {
    if (!batch) return;
    setIsRetrying((prev) => ({ ...prev, [image.id]: true }));
    try {
      await uploadConnectorService.processImages(batch.store_id, [image.id], batch.transformations);
      toast({
        title: 'Retrying image...',
        description: 'The image will be re-processed.',
      });
      // Refetch after a delay to see updated status
      setTimeout(() => fetchBatchDetail(), 2000);
    } catch (err: any) {
      toast({
        title: 'Retry failed',
        description: err.message || 'Could not retry image.',
        variant: 'destructive',
      });
    } finally {
      setIsRetrying((prev) => ({ ...prev, [image.id]: false }));
    }
  };

  // Filtered images
  const filteredImages = batch?.images?.filter((img) => {
    if (imageFilter === 'all') return true;
    if (imageFilter === 'processing') return img.status === 'processing' || img.status === 'uploaded';
    return img.status === imageFilter;
  }) || [];

  // Filter counts
  const filterCounts = {
    all: batch?.images?.length || 0,
    completed: batch?.images?.filter((i) => i.status === 'completed').length || 0,
    failed: batch?.images?.filter((i) => i.status === 'failed').length || 0,
    processing: batch?.images?.filter((i) => i.status === 'processing' || i.status === 'uploaded').length || 0,
  };

  // Calculate progress percentage
  const progressPercentage = batch && batch.total_images > 0
    ? Math.round(((batch.processed_images + batch.failed_images) / batch.total_images) * 100)
    : 0;

  // Calculate duration
  const getDuration = () => {
    if (!batch?.created_at || !batch?.completed_at) return null;
    const start = new Date(batch.created_at).getTime();
    const end = new Date(batch.completed_at).getTime();
    const diffMs = end - start;
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return `${diffSecs}s`;
    const mins = Math.floor(diffSecs / 60);
    const secs = diffSecs % 60;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <p className="mt-2 text-sm text-muted-foreground">Loading batch details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (!batch) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">Batch not found</h3>
          <p className="text-muted-foreground mb-4">
            The batch you are looking for does not exist or has been removed.
          </p>
          <Button onClick={() => navigate('/batch-history')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Batch History
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        {/* Back button */}
        <Button
          variant="ghost"
          className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/batch-history')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Batch History
        </Button>

        {/* Title row */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{batch.name || 'Untitled Batch'}</h1>
          {getStatusBadge(batch.status)}
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-6 mt-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Images className="w-4 h-4" />
            <span>{batch.total_images} total</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>{batch.processed_images} processed</span>
          </div>
          {batch.failed_images > 0 && (
            <div className="flex items-center gap-1.5">
              <XCircle className="w-4 h-4 text-red-500" />
              <span>{batch.failed_images} failed</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Zap className="w-4 h-4" />
            <span>{batch.transformation_credits} credits</span>
          </div>
        </div>

        {/* Progress bar (only for processing) */}
        {batch.status === 'processing' && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Processing...</span>
              <span className="text-muted-foreground">{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
        )}

        {/* Transformations */}
        {batch.transformations && batch.transformations.length > 0 && (
          <div className="flex items-center gap-2 mt-4">
            <span className="text-xs text-muted-foreground">Transformations:</span>
            <div className="flex flex-wrap gap-1.5">
              {batch.transformations.map((t, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {getTransformationLabel(t.type)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          <span>
            Created: {format(new Date(batch.created_at), 'PPp')}
          </span>
          {batch.completed_at && getDuration() && (
            <span>
              Duration: {getDuration()}
            </span>
          )}
        </div>

        {/* Cancel button (only for processing) */}
        {batch.status === 'processing' && (
          <div className="mt-4">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowCancelDialog(true)}
              disabled={isCancelling}
            >
              <Ban className="w-4 h-4 mr-2" />
              Cancel Batch
            </Button>
          </div>
        )}
      </div>

      {/* Image filter tabs */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {(['all', 'completed', 'failed', 'processing'] as const).map((filter) => (
          <Button
            key={filter}
            variant={imageFilter === filter ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setImageFilter(filter)}
            className={imageFilter === filter ? '' : 'text-muted-foreground'}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)} ({filterCounts[filter]})
          </Button>
        ))}
      </div>

      {/* Image thumbnail grid */}
      {filteredImages.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Images className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No {imageFilter === 'all' ? '' : imageFilter} images</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredImages.map((image) => (
            <div key={image.id} className="group">
              {/* Thumbnail */}
              <div className="relative aspect-square rounded-lg overflow-hidden border bg-muted">
                <img
                  src={image.thumbnail_url || image.original_url}
                  alt={image.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                {/* Status icon overlay */}
                {image.status === 'completed' && (
                  <div className="absolute top-2 right-2 bg-green-500/20 rounded-full p-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                )}
                {image.status === 'failed' && (
                  <div className="absolute top-2 right-2 bg-red-500/20 rounded-full p-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                )}
                {image.status === 'processing' && (
                  <div className="absolute top-2 right-2 bg-blue-500/20 rounded-full p-1">
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                  </div>
                )}
              </div>

              {/* File name */}
              <p className="text-xs text-muted-foreground mt-1.5 truncate" title={image.original_filename}>
                {image.original_filename.length > 20
                  ? image.original_filename.slice(0, 20) + '...'
                  : image.original_filename}
              </p>

              {/* Action buttons */}
              {image.status === 'completed' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-1.5 h-7 text-xs"
                  onClick={() => setCompareImage(image)}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Compare
                </Button>
              )}

              {image.status === 'failed' && (
                <div className="mt-1.5">
                  {image.error_message && (
                    <p className="text-xs text-red-500 mb-1 truncate" title={image.error_message}>
                      {image.error_message}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-7 text-xs"
                    onClick={() => handleRetry(image)}
                    disabled={isRetrying[image.id]}
                  >
                    {isRetrying[image.id] ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <RotateCcw className="w-3 h-3 mr-1" />
                    )}
                    Retry
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Cancel confirmation dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        title="Cancel Batch Processing?"
        description="Are you sure you want to cancel? Already-processed images will be kept, but remaining images will not be processed."
        confirmText="Cancel Batch"
        variant="destructive"
        isLoading={isCancelling}
        onConfirm={handleCancel}
      />

      {/* Image compare modal */}
      {compareImage && (
        <ImageCompareModal
          isOpen={!!compareImage}
          originalUrl={compareImage.original_url}
          processedUrl={compareImage.processed_url || compareImage.original_url}
          filename={compareImage.original_filename}
          transformations={batch?.transformations}
          onClose={() => setCompareImage(null)}
        />
      )}
    </div>
  );
}
